import * as React from 'react';
import { Stack, MessageBarType, MessageBarButton, concatStyleSetsWithProps } from '@fluentui/react';
import { BannerType } from '@fluidx/office-fluid-types';
import { NamespaceLoggerAdapter, sendErrorEvent } from '@fluidx/telemetry';
import { getStringTableWithFallback } from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities.dynamic';
import { ErrorEvent } from '../../../telemetry/ErrorEvent';
import { MessageBanner } from './MessageBanner';
const defaultStackConfig = {
    stackSize: 3
};
export const fluidMessageBannerStackTestId = 'FluidMessageBannerStack';
/**
 * A multi-source banner control that takes in one or more banner providers and subscribes to 'CreateBanner' and 'RemoveBanner' events.
 * Internally, it creates and customizes banners from event provided data (BannerInfo).
 * It also applies heuristics for stacking, prioritization and dismiss
 */ export const FluidMessageBanner = /*#__PURE__*/ React.memo((props)=>{
    const { bannerProviders , stackConfiguration , locale , logger , onError  } = props;
    const bannerLogger = logger && new NamespaceLoggerAdapter('ErrorUX', logger);
    const bannerStore = React.useRef(new Map());
    const [banners, setBanners] = React.useState([]);
    const [stringTable, setStringTable] = React.useState(undefined);
    React.useEffect(()=>{
        getStringTableWithFallback(getStringTable, locale).then((table)=>{
            setStringTable(table);
        }).catch(()=>{});
    }, [
        locale
    ]);
    const { stackSize , stackLayout , stackStyles , bannerStyles  } = {
        ...defaultStackConfig,
        ...stackConfiguration
    };
    const fmbStyles = concatStyleSetsWithProps(props, stackStyles);
    React.useEffect(()=>{
        if (stringTable !== undefined) {
            // Subscribe to banner sources for signals and data to create banners
            bannerProviders.map((provider)=>{
                provider.on('CreateBanner', createBannerHandler);
                provider.on('RemoveBanner', removeBannerHandler);
                // Inform the provider that the listener has been attached.
                provider.emit('BannerListenerAttached');
            });
        }
        return ()=>{
            // Dispose subscriptions
            bannerProviders.map((provider)=>{
                provider.removeListener('CreateBanner', createBannerHandler);
                provider.removeListener('RemoveBanner', removeBannerHandler);
            });
        };
    // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        bannerProviders,
        stringTable
    ]);
    const createBannerHandler = React.useCallback((bannerInfo)=>{
        const { bannerType , bannerId  } = bannerInfo;
        // Log that message banner was shown in warning/error scenarios and not when its informational or success case.
        if (bannerType === BannerType.blocked || bannerType === BannerType.error || bannerType === BannerType.severeWarning || bannerType === BannerType.warning) {
            bannerLogger && sendErrorEvent(bannerLogger, {
                eventName: ErrorEvent.MessageBannerShown,
                bannerType: BannerType[bannerType],
                ...bannerInfo.additionalFields
            }, {
                errorType: bannerId
            });
            onError?.(bannerId);
        }
        let bannersMap = bannerStore.current.get(bannerType);
        if (!bannersMap) {
            bannersMap = new Map();
            bannerStore.current.set(bannerType, bannersMap);
        }
        bannersMap.set(bannerId, bannerInfo);
        setBanners(getBannerStack());
    }, // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        bannerStore.current,
        stringTable
    ]);
    const removeBannerHandler = React.useCallback((bannerId)=>{
        for (const bannersMap of bannerStore.current.values()){
            if (bannersMap.has(bannerId)) {
                // Remove rendered banner with provided id
                bannersMap.delete(bannerId);
                setBanners(getBannerStack());
                return;
            }
        }
    }, // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        bannerStore.current,
        stringTable
    ]);
    const getBannerStack = React.useCallback(()=>{
        const bannerInfoStore = bannerStore.current;
        const errorBanner = getPreferredBannerForSlot(BannerType.error, bannerInfoStore);
        const blockedBanner = getPreferredBannerForSlot(BannerType.blocked, bannerInfoStore);
        const severeWarningBanner = getPreferredBannerForSlot(BannerType.severeWarning, bannerInfoStore);
        const warningBanner = getPreferredBannerForSlot(BannerType.warning, bannerInfoStore);
        const infoBanner = getPreferredBannerForSlot(BannerType.info, bannerInfoStore);
        const successBanner = getPreferredBannerForSlot(BannerType.success, bannerInfoStore);
        switch(stackSize){
            case 1:
                const priorityBanner = errorBanner || blockedBanner || severeWarningBanner || warningBanner || infoBanner || successBanner;
                // TODO: Consider alternative prioritization of banners: like using timestamps for firstBanner or lastBanner stack order modes
                return [
                    ...priorityBanner ? [
                        priorityBanner
                    ] : []
                ];
            case 6:
                return [
                    ...errorBanner ? [
                        errorBanner
                    ] : [],
                    ...blockedBanner ? [
                        blockedBanner
                    ] : [],
                    ...severeWarningBanner ? [
                        severeWarningBanner
                    ] : [],
                    ...warningBanner ? [
                        warningBanner
                    ] : [],
                    ...infoBanner ? [
                        infoBanner
                    ] : [],
                    ...successBanner ? [
                        successBanner
                    ] : []
                ];
            case 3:
            default:
                const criticalBannerSlot = errorBanner || blockedBanner;
                const warningBannerSlot = severeWarningBanner || warningBanner;
                const infoBannerSlot = infoBanner || successBanner;
                return [
                    ...criticalBannerSlot ? [
                        criticalBannerSlot
                    ] : [],
                    ...warningBannerSlot ? [
                        warningBannerSlot
                    ] : [],
                    ...infoBannerSlot ? [
                        infoBannerSlot
                    ] : []
                ];
        }
    // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        bannerStore.current,
        stringTable
    ]);
    const getPreferredBannerForSlot = React.useCallback((bannerType, bannerStore)=>{
        const bannerInfosMap = bannerStore.get(bannerType);
        // Exception added when eslint rule was added, this should be revisited when modifying this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!bannerInfosMap || bannerInfosMap?.size < 1) {
            return null;
        }
        const bannerTypeDefaults = getBannerTypeDefaults(bannerType);
        // Return newest banner available
        return createBannerElement({
            ...bannerTypeDefaults,
            ...[
                ...bannerInfosMap.values()
            ].reverse()[0]
        });
    }, // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        stringTable
    ]);
    const createBannerElement = React.useCallback(({ bannerId , bannerType , bannerStrings , actions , icon , isMultiline , isAssertive , isDismissible , bannerLink  })=>{
        const onBannerDismiss = isDismissible ? ()=>{
            removeBannerHandler(bannerId);
        } : undefined;
        const dismissAriaLabel = stringTable?.close;
        // Create banner element from provided info
        return /*#__PURE__*/ React.createElement(Stack.Item, {
            key: bannerId
        }, /*#__PURE__*/ React.createElement(MessageBanner, {
            messageBarType: getMessageBarType(bannerType),
            bannerStrings: bannerStrings,
            actions: actions?.map((action)=>getBannerAction(action)),
            icon: icon,
            isMultiline: isMultiline,
            isAssertive: isAssertive,
            // eslint-disable-next-line react/jsx-no-bind
            onDismiss: onBannerDismiss,
            dismissButtonAriaLabel: dismissAriaLabel,
            styles: bannerStyles,
            hyperlink: bannerLink
        }));
    }, // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        stringTable
    ]);
    return /*#__PURE__*/ React.createElement(Stack, {
        ...stackLayout,
        styles: fmbStyles,
        "data-testid": fluidMessageBannerStackTestId
    }, banners);
});
const getBannerAction = ({ actionCallback , label  })=>{
    return /*#__PURE__*/ React.createElement(MessageBarButton, {
        onClick: actionCallback,
        styles: {
            root: {
                width: 'auto'
            }
        },
        ariaLabel: label,
        key: label
    }, label);
};
const getMessageBarType = (bannerType)=>{
    switch(bannerType){
        case BannerType.info:
            return MessageBarType.info;
        case BannerType.error:
            return MessageBarType.error;
        case BannerType.blocked:
            return MessageBarType.blocked;
        case BannerType.severeWarning:
            return MessageBarType.severeWarning;
        case BannerType.success:
            return MessageBarType.success;
        case BannerType.warning:
            return MessageBarType.warning;
    }
};
const getBannerTypeDefaults = (bannerType)=>{
    // TODO: Make banners single line by default, with expand / see more button
    const isMultiline = false;
    switch(bannerType){
        case BannerType.error:
        case BannerType.blocked:
            return {
                isAssertive: true,
                isDismissible: false,
                isMultiline
            };
        case BannerType.severeWarning:
        case BannerType.warning:
            return {
                isAssertive: false,
                isDismissible: true,
                isMultiline
            };
        case BannerType.info:
        case BannerType.success:
            return {
                isAssertive: false,
                isDismissible: true,
                isMultiline
            };
    }
}; // cspell:ignore customizer testid
